const ZIP_REGEX = /^\d{5}$/;

export const LOCATION_STORAGE_KEYS = Object.freeze({
  detectedRegionId: "homehelper_detected_region_id",
  detectedRegionSlug: "homehelper_detected_region_slug",
  detectedRegionName: "homehelper_detected_region_name",
  detectedZip: "homehelper_detected_zip",
  locationSource: "homehelper_location_source",
  locationConfidence: "homehelper_location_confidence",
  locationConfirmedAt: "homehelper_location_confirmed_at",
  locationRequiresConfirmation: "homehelper_location_requires_confirmation",
  confirmedRegionId: "homehelper_confirmed_region_id",
  confirmedRegionSlug: "homehelper_confirmed_region_slug",
  confirmedRegionName: "homehelper_confirmed_region_name",
  confirmedZip: "homehelper_confirmed_zip",
});

function safeGetItem(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSetItem(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // ignore storage errors
  }
}

function safeRemoveItem(key) {
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore storage errors
  }
}

function normalizeZip(value) {
  const zip = String(value || "").trim();
  return ZIP_REGEX.test(zip) ? zip : "";
}

function normalizeString(value) {
  const normalized = String(value || "").trim();
  return normalized || "";
}

function normalizeBoolean(value, fallback = false) {
  if (typeof value === "boolean") {
    return value;
  }

  if (value == null) {
    return fallback;
  }

  return String(value).trim().toLowerCase() === "true";
}

export function getDetectedLocationContext() {
  return {
    regionId: normalizeString(safeGetItem(LOCATION_STORAGE_KEYS.detectedRegionId)),
    regionSlug: normalizeString(safeGetItem(LOCATION_STORAGE_KEYS.detectedRegionSlug)),
    regionName: normalizeString(safeGetItem(LOCATION_STORAGE_KEYS.detectedRegionName)),
    zip: normalizeZip(safeGetItem(LOCATION_STORAGE_KEYS.detectedZip)),
    source: normalizeString(safeGetItem(LOCATION_STORAGE_KEYS.locationSource)),
    confidence: normalizeString(safeGetItem(LOCATION_STORAGE_KEYS.locationConfidence)),
    confirmedAt: normalizeString(safeGetItem(LOCATION_STORAGE_KEYS.locationConfirmedAt)),
    requiresConfirmation: normalizeBoolean(
      safeGetItem(LOCATION_STORAGE_KEYS.locationRequiresConfirmation),
      true
    ),
  };
}

export function getConfirmedLocationContext() {
  return {
    regionId: normalizeString(safeGetItem(LOCATION_STORAGE_KEYS.confirmedRegionId)),
    regionSlug: normalizeString(safeGetItem(LOCATION_STORAGE_KEYS.confirmedRegionSlug)),
    regionName: normalizeString(safeGetItem(LOCATION_STORAGE_KEYS.confirmedRegionName)),
    zip: normalizeZip(
      safeGetItem(LOCATION_STORAGE_KEYS.confirmedZip) ||
        safeGetItem("hh_zip") ||
        safeGetItem("user_zip")
    ),
    confirmedAt: normalizeString(safeGetItem(LOCATION_STORAGE_KEYS.locationConfirmedAt)),
    source: normalizeString(safeGetItem(LOCATION_STORAGE_KEYS.locationSource)),
    confidence: normalizeString(safeGetItem(LOCATION_STORAGE_KEYS.locationConfidence)),
  };
}

export function persistDetectedLocation({
  regionId = "",
  regionSlug = "",
  regionName = "",
  zip = "",
  source = "detected",
  confidence = "",
  requiresConfirmation = true,
} = {}) {
  safeSetItem(LOCATION_STORAGE_KEYS.detectedRegionId, normalizeString(regionId));
  safeSetItem(LOCATION_STORAGE_KEYS.detectedRegionSlug, normalizeString(regionSlug));
  safeSetItem(LOCATION_STORAGE_KEYS.detectedRegionName, normalizeString(regionName));

  const normalizedZip = normalizeZip(zip);
  if (normalizedZip) {
    safeSetItem(LOCATION_STORAGE_KEYS.detectedZip, normalizedZip);
  } else {
    safeRemoveItem(LOCATION_STORAGE_KEYS.detectedZip);
  }

  safeSetItem(LOCATION_STORAGE_KEYS.locationSource, normalizeString(source));
  safeSetItem(LOCATION_STORAGE_KEYS.locationConfidence, normalizeString(confidence));
  safeSetItem(
    LOCATION_STORAGE_KEYS.locationRequiresConfirmation,
    requiresConfirmation ? "true" : "false"
  );
}

export function clearDetectedLocation() {
  safeRemoveItem(LOCATION_STORAGE_KEYS.detectedRegionId);
  safeRemoveItem(LOCATION_STORAGE_KEYS.detectedRegionSlug);
  safeRemoveItem(LOCATION_STORAGE_KEYS.detectedRegionName);
  safeRemoveItem(LOCATION_STORAGE_KEYS.detectedZip);
  safeRemoveItem(LOCATION_STORAGE_KEYS.locationRequiresConfirmation);
}

export function persistConfirmedLocation({
  regionId = "",
  regionSlug = "",
  regionName = "",
  zip = "",
  source = "confirmed",
  confidence = "",
} = {}) {
  const normalizedZip = normalizeZip(zip);

  safeSetItem(LOCATION_STORAGE_KEYS.confirmedRegionId, normalizeString(regionId));
  safeSetItem(LOCATION_STORAGE_KEYS.confirmedRegionSlug, normalizeString(regionSlug));
  safeSetItem(LOCATION_STORAGE_KEYS.confirmedRegionName, normalizeString(regionName));
  safeSetItem(LOCATION_STORAGE_KEYS.locationSource, normalizeString(source));
  safeSetItem(LOCATION_STORAGE_KEYS.locationConfidence, normalizeString(confidence));
  safeSetItem(LOCATION_STORAGE_KEYS.locationConfirmedAt, new Date().toISOString());
  safeSetItem(LOCATION_STORAGE_KEYS.locationRequiresConfirmation, "false");

  if (normalizedZip) {
    safeSetItem(LOCATION_STORAGE_KEYS.confirmedZip, normalizedZip);
    safeSetItem("hh_zip", normalizedZip);
    safeSetItem("user_zip", normalizedZip);
  }

  if (regionSlug) {
    safeSetItem("homehelper_region_slug", normalizeString(regionSlug));
  }

  if (regionName) {
    safeSetItem("homehelper_region_name", normalizeString(regionName));
  }
}

export function clearConfirmedLocation() {
  safeRemoveItem(LOCATION_STORAGE_KEYS.confirmedRegionId);
  safeRemoveItem(LOCATION_STORAGE_KEYS.confirmedRegionSlug);
  safeRemoveItem(LOCATION_STORAGE_KEYS.confirmedRegionName);
  safeRemoveItem(LOCATION_STORAGE_KEYS.confirmedZip);
  safeRemoveItem(LOCATION_STORAGE_KEYS.locationConfirmedAt);
}

export function getPreferredNavigationZip() {
  const confirmed = getConfirmedLocationContext();
  if (confirmed.zip) {
    return confirmed.zip;
  }

  return normalizeZip(safeGetItem("hh_zip") || safeGetItem("user_zip"));
}

export function getPreferredCategoryPath(categorySlug) {
  const normalizedCategorySlug = normalizeString(categorySlug);
  if (!normalizedCategorySlug) {
    return "/in-town";
  }

  const confirmed = getConfirmedLocationContext();
  if (confirmed.zip) {
    return `/${confirmed.zip}/${normalizedCategorySlug}`;
  }

  if (confirmed.regionSlug) {
    return `/${confirmed.regionSlug}/${normalizedCategorySlug}`;
  }

  const detected = getDetectedLocationContext();
  if (detected.regionSlug) {
    return `/${detected.regionSlug}/${normalizedCategorySlug}`;
  }

  return `/in-town/${normalizedCategorySlug}`;
}
